/**
 * Landing Page V2 - Hero Section
 *
 * Main hero section with greeting and search
 * 100% Design System Integration - NO hardcoded values
 */

'use client';

import React from 'react';
import { useRouter } from 'next/router';
import { ChatInput } from '@/design-system';
import { ChatInputMobile } from '@/design-system/components/ChatInput/ChatInputMobile';
import {
  typographyColors,
  backgroundColors,
  spacing,
  dimensions,
  borders,
  stylePresets,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import Image from 'next/image';
import SearchSuggestionList from './SearchSuggestionList';
import { ScrollReveal } from './ScrollReveal';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useLocationPermission } from '@/hooks/useLocationPermission';
import { useUserStore } from '@/store/userStore';

const BOTTOM_BUFFER = 48;

export function HeroSection() {
  const [searchValue, setSearchValue] = React.useState('');
  const [isMobile, setIsMobile] = React.useState(false);
  const [spaceBelow, setSpaceBelow] = React.useState<number | undefined>(
    undefined
  );
  const searchInputWrapRef = React.useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { hasRequested, requestPermission } = useLocationPermission();
  const { name } = useUserStore();

  // Mobile detection
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // On mobile: measure vertical space below search input once (and on resize only) so suggestion count doesn't change on scroll
  React.useEffect(() => {
    if (!isMobile) {
      setSpaceBelow(undefined);
      return;
    }
    const measure = () => {
      const el = searchInputWrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - BOTTOM_BUFFER;
      setSpaceBelow(Math.max(0, Math.floor(below)));
    };
    const el = searchInputWrapRef.current;
    if (el) {
      measure();
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const id = requestAnimationFrame(() => measure());
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', measure);
    };
  }, [isMobile]);

  // Get display name for greeting
  const isDefaultName = name === 'User Name';
  const displayName = isDefaultName
    ? 'Traveller'
    : name?.split(' ')[0] || 'Traveller';

  // Request location permission on first visit
  React.useEffect(() => {
    if (!hasRequested) {
      // Small delay to ensure the page is fully loaded
      const timer = setTimeout(() => {
        requestPermission();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasRequested, requestPermission]);

  // Use the audio recorder hook for landing page
  const {
    isRecording,
    recordingTime,
    startRecording,
    stopRecording,
    isActuallyRecording,
    cleanup: cleanupAudioRecorder,
  } = useAudioRecorder();

  // Cleanup audio recorder on unmount
  React.useEffect(() => {
    return () => {
      cleanupAudioRecorder();
    };
  }, [cleanupAudioRecorder]);

  const handlePromptSelect = async (prompt: string) => {
    // First, set the text in the input immediately so it appears with font styles
    setSearchValue(prompt);

    // Small delay to ensure the UI updates before navigating
    await new Promise(resolve => setTimeout(resolve, 50));

    // Navigate immediately to chat screen with query
    // The chat screen will handle the API call and show placeholder loading
    router.push(`/chat?q=${encodeURIComponent(prompt)}`);
  };

  const handleInputChange = (value: string) => {
    setSearchValue(value);
  };

  const handleSubmit = async () => {
    if (!searchValue.trim()) return;

    const queryText = searchValue.trim();
    // Navigate immediately to chat screen with query
    // The chat screen will handle the API call and show placeholder loading
    router.push(`/chat?q=${encodeURIComponent(queryText)}`);
  };

  const handleMicClick = async () => {
    console.log('═══════════════════════════════════════════════════════');
    console.log('🎤 [LANDING PAGE] handleMicClick TRIGGERED');
    console.log('═══════════════════════════════════════════════════════');
    console.log('🎤 [LANDING PAGE] React isRecording state:', isRecording);

    // IMPORTANT: Check actual recording state, not just React state
    // This prevents issues with Fast Refresh/Hot Reload resetting state
    const actuallyRecording = isActuallyRecording();
    console.log('🎤 [LANDING PAGE] Actually recording?', actuallyRecording);

    if (actuallyRecording !== isRecording) {
      console.warn('⚠️ [LANDING PAGE] STATE MISMATCH DETECTED!');
      console.warn(
        '   React state says:',
        isRecording ? 'recording' : 'not recording'
      );
      console.warn(
        '   MediaRecorder says:',
        actuallyRecording ? 'recording' : 'not recording'
      );
      console.warn('   Using actual MediaRecorder state to decide action');
    }

    if (actuallyRecording) {
      console.log('✅ [LANDING PAGE] isRecording is TRUE, will stop recording');
      // Stop recording and navigate with audio
      try {
        console.log('🎤 [LANDING PAGE] Calling stopRecording()...');
        const audioBase64 = await stopRecording();
        console.log('🎤 [LANDING PAGE] stopRecording() returned');
        console.log('🎤 [LANDING PAGE] Audio base64 received:', {
          hasAudio: !!audioBase64,
          audioLength: audioBase64?.length || 0,
          audioType: typeof audioBase64,
          isNull: audioBase64 === null,
          isUndefined: audioBase64 === undefined,
          firstChars: audioBase64?.substring(0, 30),
        });

        if (audioBase64) {
          console.log(
            '✅ [LANDING PAGE] Audio received, storing in sessionStorage'
          );
          // Store audio in sessionStorage and navigate immediately
          const audioKey = `pending_audio_${Date.now()}`;
          sessionStorage.setItem(audioKey, audioBase64);
          console.log('✅ [LANDING PAGE] Audio stored with key:', audioKey);

          const queryParam = searchValue.trim()
            ? `&q=${encodeURIComponent(searchValue.trim())}`
            : '';
          const navigateUrl = `/chat?audioKey=${audioKey}${queryParam}`;
          console.log('🚀 [LANDING PAGE] Navigating to:', navigateUrl);

          // Navigate to chat screen - it will handle the API call and show placeholder loading
          router.push(navigateUrl);
        } else {
          console.error(
            '❌ [LANDING PAGE] No audio received, navigating without audio'
          );
          router.push('/chat');
        }
      } catch (err) {
        console.error('❌ [LANDING PAGE] Error in handleMicClick:', err);
        router.push('/chat');
      }
    } else {
      console.log(
        '⚠️ [LANDING PAGE] isRecording is FALSE, will start recording'
      );
      // Start recording
      try {
        console.log('🎤 [LANDING PAGE] Calling startRecording()...');
        await startRecording();
        console.log('✅ [LANDING PAGE] startRecording() completed');
      } catch (err) {
        console.error('❌ [LANDING PAGE] Error starting recording:', err);
      }
    }
  };

  const handleStopClick = async () => {
    console.log('═══════════════════════════════════════════════════════');
    console.log(
      '⏹️ [LANDING PAGE] handleStopClick TRIGGERED (Stop button clicked)'
    );
    console.log('═══════════════════════════════════════════════════════');
    console.log('⏹️ [LANDING PAGE] React isRecording state:', isRecording);

    // Check actual recording state
    const actuallyRecording = isActuallyRecording();
    console.log('⏹️ [LANDING PAGE] Actually recording?', actuallyRecording);

    if (actuallyRecording) {
      console.log('✅ [LANDING PAGE] Stopping and saving recording...');
      // Stop recording and navigate with audio (same as handleMicClick)
      try {
        console.log('⏹️ [LANDING PAGE] Calling stopRecording()...');
        const audioBase64 = await stopRecording();
        console.log('⏹️ [LANDING PAGE] stopRecording() returned');
        console.log('⏹️ [LANDING PAGE] Audio base64 received:', {
          hasAudio: !!audioBase64,
          audioLength: audioBase64?.length || 0,
          audioType: typeof audioBase64,
          isNull: audioBase64 === null,
          isUndefined: audioBase64 === undefined,
          firstChars: audioBase64?.substring(0, 30),
        });

        if (audioBase64) {
          console.log(
            '✅ [LANDING PAGE] Audio received from stop button, storing in sessionStorage'
          );
          // Store audio in sessionStorage and navigate immediately
          const audioKey = `pending_audio_${Date.now()}`;
          sessionStorage.setItem(audioKey, audioBase64);
          console.log('✅ [LANDING PAGE] Audio stored with key:', audioKey);

          const queryParam = searchValue.trim()
            ? `&q=${encodeURIComponent(searchValue.trim())}`
            : '';
          const navigateUrl = `/chat?audioKey=${audioKey}${queryParam}`;
          console.log('🚀 [LANDING PAGE] Navigating to:', navigateUrl);

          // Navigate to chat screen - it will handle the API call
          router.push(navigateUrl);
        } else {
          console.error(
            '❌ [LANDING PAGE] No audio received from stop button, navigating without audio'
          );
          router.push('/chat');
        }
      } catch (err) {
        console.error('❌ [LANDING PAGE] Error in handleStopClick:', err);
        router.push('/chat');
      }
    } else {
      console.warn('⚠️ [LANDING PAGE] Stop button clicked but not recording');
    }
  };

  return (
    <ScrollReveal
      as='section'
      className='flex flex-col items-center justify-center px-4 md:px-4 py-4 md:py-16'
      style={{
        paddingTop: spacing[0],
        paddingBottom: isMobile ? spacing[6] : spacing[4], // More bottom gap on mobile to prevent overlap with Explore section below
        paddingLeft: spacing[4],
        paddingRight: spacing[4],
        flex: '0 0 auto',
        minHeight: '60vh',
        maxHeight: '70vh',
      }}
      amount={0.2}
      direction='up'
      margin='0% 0% -10% 0%'
    >
      {/* Page title for SEO - visible to crawlers, hidden visually so Google shows "Nexgo AI" like Perplexity AI */}
      <h1 className='sr-only'>Nexgo AI</h1>
      <ScrollReveal
        className='flex flex-col items-center w-full'
        style={{
          maxWidth: dimensions.container.hero,
        }}
        delay={0.1}
      >
        {/* Greeting Section */}
        <ScrollReveal
          className='flex flex-col items-center justify-center mt-6 md:mt-0'
          style={{
            marginBottom: spacing[2], // Reduced from dimensions.gaps.greeting
          }}
          delay={0.2}
        >
          {/* Image Container */}
          <div
            className='relative shrink-0 mt-8 md:mt-0'
            style={{
              width: stylePresets.heroImage.width,
              height: stylePresets.heroImage.height,
            }}
          >
            <Image
              src='/assets/images/nexgi_cover.svg'
              alt='Nexgo'
              fill
              className='object-cover'
            />
          </div>

          {/* Greeting Text */}
          <div
            className='text-center text-xl md:text-3xl'
            style={{
              fontFamily: fontPrimary,
              fontWeight: typography.weight.semibold,
              fontStyle: 'normal',
              lineHeight: 'normal',
              color: typographyColors.text,
              textAlign: 'center',
              marginTop: isMobile ? spacing[4] : spacing[2], // More spacing on mobile
              marginBottom: isMobile ? spacing[3] : 0, // Add bottom margin on mobile
              padding: isMobile ? `${spacing[2]}px` : 0, // Add padding on mobile
            }}
          >
            <p style={{ marginBottom: 0, lineHeight: 'normal' }}>
              Hello {displayName},
            </p>
            <p style={{ lineHeight: 'normal', margin: 0 }}>
              <span>let&apos;s </span>
              <span
                className='text-xl md:text-3xl'
                style={{
                  color: typographyColors.text,
                  fontFamily: fontPrimary,
                  fontStyle: 'normal',
                  fontWeight: typography.weight.semibold,
                  lineHeight: 'normal',
                  textDecorationLine: 'line-through',
                }}
              >
                talk
              </span>
              <span> travel!</span>
            </p>
          </div>
        </ScrollReveal>

        {/* Search Section */}
        <ScrollReveal
          className='w-full flex flex-col'
          style={{ gap: isMobile ? spacing[4] : spacing[3] }} // More gap on mobile
          delay={0.3}
        >
          {/* Main Chat with disclaimer */}
          <ScrollReveal
            className='w-full flex flex-col'
            style={{ gap: spacing[0.5] }}
            delay={0.4}
          >
            <div ref={searchInputWrapRef} className='w-full flex flex-col'>
              {isMobile ? (
                <ChatInputMobile
                  placeholder='Ask anything about travel.'
                  showMicrophone={true}
                  showAttachment={false}
                  value={searchValue}
                  onChange={handleInputChange}
                  onSubmit={handleSubmit}
                  onMicClick={handleMicClick}
                  onStopClick={handleStopClick}
                  isRecording={isRecording}
                  recordingTime={recordingTime}
                  isLoading={false}
                  disabled={false}
                  onAttachClick={() => console.log('Attachment clicked')}
                  inputFontSize='14px'
                />
              ) : (
                <ChatInput
                  placeholder='Ask anything about travel.'
                  showMicrophone={true}
                  showAttachment={false}
                  value={searchValue}
                  onChange={handleInputChange}
                  onSubmit={handleSubmit}
                  onMicClick={handleMicClick}
                  onStopClick={handleStopClick}
                  isRecording={isRecording}
                  recordingTime={recordingTime}
                  isLoading={false}
                  disabled={false}
                  onAttachClick={() => console.log('Attachment clicked')}
                  inputFontSize='14px'
                />
              )}
            </div>
          </ScrollReveal>

          {/* Suggested Searches Header */}
          <ScrollReveal
            className='hidden md:flex items-center w-full'
            style={{
              gap: dimensions.gaps.divider,
            }}
            delay={0.45}
            direction='none'
          >
            <div
              className='flex-1'
              style={{
                height: borders.width.thin,
                backgroundColor: backgroundColors.shade,
              }}
            />
            <span
              style={{
                fontFamily: typography.fonts.secondary,
                fontWeight: typography.weight.semibold,
                fontSize: typography.size.sm,
                lineHeight: '20px',
                color: typographyColors.text,
                whiteSpace: 'nowrap',
              }}
            >
              Suggested Searches
            </span>
            <div
              className='flex-1'
              style={{
                height: borders.width.thin,
                backgroundColor: backgroundColors.shade,
              }}
            />
          </ScrollReveal>

          {/* Search suggestions */}
          <ScrollReveal delay={0.5} direction='up'>
            <SearchSuggestionList
              count={4}
              availableHeight={isMobile ? spaceBelow : undefined}
              className='w-full'
              onPromptClick={handlePromptSelect}
            />
          </ScrollReveal>
        </ScrollReveal>
      </ScrollReveal>
    </ScrollReveal>
  );
}
